import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit, Eye, Loader2, BookOpen, Share2, Sparkles, Copy, Check, ImageIcon, Download } from 'lucide-react';
import { formatApiErrorDetail } from '@/utils/api';
import StoryPostcard from '@/components/StoryPostcard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoryLibrary = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [illustratingId, setIllustratingId] = useState(null);
  const [upgradeNeeded, setUpgradeNeeded] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  const [isPostcardOpen, setIsPostcardOpen] = useState(false);
  const [postcardStory, setPostcardStory] = useState(null);

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStories = async () => {
    try {
      const { data } = await axios.get(`${API}/stories`, {
        withCredentials: true,
      });
      setStories(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (story) => {
    setSelectedStory(story);
    setIsViewOpen(true);
  };

  const handleShare = async (story) => {
    setSharingId(story.id);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/stories/${story.id}/share`,
        {},
        { withCredentials: true }
      );
      const url = `${window.location.origin}/share/${data.share_token}`;
      setShareUrl(url);
      setSelectedStory(story);
      setIsShareOpen(true);
      setCopied(false);
      // Update story in state with new share_token
      setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, share_token: data.share_token } : s)));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSharingId(null);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleIllustrate = async (story) => {
    setIllustratingId(story.id);
    setError('');
    setUpgradeNeeded(false);
    try {
      const { data } = await axios.post(
        `${API}/stories/${story.id}/illustrate`,
        {},
        { withCredentials: true, timeout: 60000 }
      );
      // Update local state with new illustration
      setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, illustration: data.illustration } : s)));
    } catch (err) {
      if ([403, 429].includes(err.response?.status)) setUpgradeNeeded(true);
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not generate illustration');
    } finally {
      setIllustratingId(null);
    }
  };

  const handleEdit = (story) => {
    setSelectedStory(story);
    setEditTitle(story.title);
    setEditContent(story.content);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError('');

    try {
      await axios.put(
        `${API}/stories/${selectedStory.id}`,
        {
          title: editTitle.trim(),
          content: editContent.trim(),
        },
        { withCredentials: true }
      );
      setIsEditOpen(false);
      fetchStories();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) {
      return;
    }

    try {
      await axios.delete(`${API}/stories/${storyId}`, {
        withCredentials: true,
      });
      fetchStories();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="library-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="text-center py-12" data-testid="library-empty">
        <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No stories yet</h3>
        <p className="text-gray-500">Start by generating a prompt and writing your first story!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="story-library">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex flex-wrap items-center justify-between gap-2" data-testid="library-error">
          <span>{error}</span>
          {upgradeNeeded && (
            <Link to="/pricing" className="font-semibold underline underline-offset-2 hover:text-red-900" data-testid="library-error-upgrade-link">See Writer plan</Link>
          )}
        </div>
      )}

      <div className="grid gap-4">
        {stories.map((story) => (
          <Card key={story.id} className="hover:shadow-md transition-shadow border-amber-100" data-testid={`story-card-${story.id}`}>
            <div className="flex flex-col md:flex-row">
              {story.illustration && (
                <div className="md:w-40 md:flex-shrink-0 p-4 md:pr-0">
                  <img
                    src={story.illustration}
                    alt={`Illustration for ${story.title}`}
                    className="w-full h-32 md:h-full object-cover rounded-lg ring-1 ring-amber-200"
                    data-testid={`story-illustration-${story.id}`}
                  />
                </div>
              )}
              <div className="flex-1">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="font-display text-lg text-amber-950" data-testid={`story-title-${story.id}`}>{story.title}</CardTitle>
                      <CardDescription>
                        {story.category && (
                          <span className="inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded mr-2 capitalize">
                            {story.category}
                          </span>
                        )}
                        <span className="text-gray-500">{story.word_count} words</span>
                        <span className="mx-2">•</span>
                        <span className="text-gray-500">{formatDate(story.created_at)}</span>
                        {story.share_token && (
                          <>
                            <span className="mx-2">•</span>
                            <span className="text-emerald-600 text-xs">Shared</span>
                            <span className="mx-2">•</span>
                            <span className="text-gray-500 text-xs" data-testid={`story-views-${story.id}`}>{story.views || 0} {story.views === 1 ? 'view' : 'views'}</span>
                            <span className="mx-2">•</span>
                            <span className="text-rose-500 text-xs" data-testid={`story-hearts-${story.id}`}>{story.reactions || 0} {story.reactions === 1 ? 'heart' : 'hearts'}</span>
                          </>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  {story.prompt && (
                    <p className="text-sm text-gray-600 italic mt-2">
                      Prompt: &ldquo;{story.prompt.substring(0, 100)}{story.prompt.length > 100 ? '...' : ''}&rdquo;
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleView(story)}
                      data-testid={`view-story-button-${story.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(story)}
                      data-testid={`edit-story-button-${story.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShare(story)}
                      disabled={sharingId === story.id}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`share-story-button-${story.id}`}
                    >
                      {sharingId === story.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Share2 className="h-4 w-4 mr-1" />
                      )}
                      Share
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleIllustrate(story)}
                      disabled={illustratingId === story.id}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`illustrate-story-button-${story.id}`}
                    >
                      {illustratingId === story.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Drawing...
                        </>
                      ) : (
                        <>
                          {story.illustration ? <ImageIcon className="h-4 w-4 mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                          {story.illustration ? 'Re-illustrate' : 'Illustrate'}
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPostcardStory(story);
                        setIsPostcardOpen(true);
                      }}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`postcard-story-button-${story.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Postcard
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(story.id)}
                      data-testid={`delete-story-button-${story.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedStory && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="view-story-title">{selectedStory.title}</DialogTitle>
                <div className="flex space-x-2 text-sm text-gray-500">
                  {selectedStory.category && (
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                      {selectedStory.category}
                    </span>
                  )}
                  <span>{selectedStory.word_count} words</span>
                </div>
              </DialogHeader>
              <div className="mt-4">
                {selectedStory.illustration && (
                  <div className="mb-4">
                    <img
                      src={selectedStory.illustration}
                      alt="Story illustration"
                      className="w-full rounded-lg ring-1 ring-amber-200"
                    />
                  </div>
                )}
                {selectedStory.prompt && (
                  <div className="bg-amber-50 p-3 rounded mb-4 border border-amber-100">
                    <p className="text-sm font-medium text-amber-900">Prompt:</p>
                    <p className="text-amber-700 italic text-sm">&ldquo;{selectedStory.prompt}&rdquo;</p>
                  </div>
                )}
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap font-serif leading-relaxed" data-testid="view-story-content">
                    {selectedStory.content}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedStory && (
            <>
              <DialogHeader>
                <DialogTitle>Edit Story</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    data-testid="edit-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-content">Content</Label>
                  <Textarea
                    id="edit-content"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={15}
                    className="font-serif"
                    data-testid="edit-content-textarea"
                  />
                  <p className="text-xs text-gray-500">
                    {editContent.trim().split(/\s+/).filter((w) => w.length > 0).length} words
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsEditOpen(false)}
                  data-testid="cancel-edit-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-edit-button"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-950">Share your story</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-amber-800/80">
              Anyone with this link can read your story. Copy it and share far and wide.
            </p>
            <div className="flex space-x-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 font-mono text-xs"
                data-testid="share-url-input"
              />
              <Button
                onClick={handleCopyLink}
                className={copied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-900 hover:bg-amber-800'}
                data-testid="copy-share-link-button"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-amber-700 hover:underline text-center"
              data-testid="preview-share-link"
            >
              Preview in new tab &rarr;
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Postcard preview & download */}
      <StoryPostcard
        story={postcardStory}
        open={isPostcardOpen}
        onOpenChange={setIsPostcardOpen}
      />
    </div>
  );
};

export default StoryLibrary;
